/**
 * RunsPanel — collapsible left-side history of simulator runs.
 *
 * Replaces the old node palette (the "+" / right-click flows now own
 * node-add). Collapsed: a thin vertical strip with a vertical "Runs" label
 * and the count of runs. Expanded: scrollable list of runs with timestamp
 * and success/error chip; click clears all.
 */

import { useEffect } from "react";
import { ChevronLeft, ChevronRight, History, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { clsx } from "clsx";
import { useEditorStore } from "../designer/editorStore";
import { useRunsStore } from "./runsStore";

export function RunsPanel() {
  const flowId = useEditorStore((s) => s.flowId);
  const expanded = useRunsStore((s) => s.expanded);
  const setExpanded = useRunsStore((s) => s.setExpanded);
  const runs = useRunsStore((s) => s.runs);
  const loadForFlow = useRunsStore((s) => s.loadForFlow);
  const clearForCurrentFlow = useRunsStore((s) => s.clearForCurrentFlow);

  useEffect(() => {
    if (flowId) loadForFlow(flowId);
  }, [flowId, loadForFlow]);

  if (!expanded) {
    return (
      <aside
        className="w-9 shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 flex flex-col items-center py-2"
        aria-label="Runs panel (collapsed)"
      >
        <button
          onClick={() => setExpanded(true)}
          className="w-7 h-7 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-900 flex items-center justify-center text-neutral-500"
          title="Expand runs"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <div className="mt-3 flex flex-col items-center gap-1 text-neutral-500">
          <History className="w-4 h-4" />
          <span
            className="text-[10px] font-mono"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Runs · {runs.length}
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="w-64 shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 flex flex-col"
      aria-label="Runs panel"
    >
      <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            Recent runs
          </p>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            {runs.length === 0
              ? "No runs yet"
              : `${runs.length} run${runs.length === 1 ? "" : "s"} stored`}
          </p>
        </div>
        <button
          onClick={() => setExpanded(false)}
          className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          title="Collapse"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {runs.length === 0 ? (
          <p className="px-3 py-4 text-xs text-neutral-500">
            Open the simulator (top toolbar) and run the flow to see entries
            here.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {runs.map((r) => (
              <RunRow key={r.id} run={r} />
            ))}
          </ul>
        )}
      </div>
      {runs.length > 0 ? (
        <div className="p-2 border-t border-neutral-200 dark:border-neutral-800">
          <button
            onClick={clearForCurrentFlow}
            className="w-full flex items-center justify-center gap-1 text-[11px] text-neutral-500 hover:text-category-error py-1"
          >
            <Trash2 className="w-3 h-3" />
            Clear history
          </button>
        </div>
      ) : null}
    </aside>
  );
}

function RunRow({ run }: { run: { timestamp: string; status: "success" | "error"; endNodeId?: string; pathLength?: number; errorCode?: string; errorMessage?: string } }) {
  const date = new Date(run.timestamp);
  const dateStr = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const timeStr = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const ok = run.status === "success";
  return (
    <li className="px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-900">
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-category-trigger shrink-0" />
        ) : (
          <XCircle className="w-3.5 h-3.5 text-category-error shrink-0" />
        )}
        <span className="text-xs font-medium">{dateStr}</span>
        <span className="text-xs text-neutral-500 font-mono">{timeStr}</span>
        <span
          className={clsx(
            "ml-auto text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded",
            ok
              ? "bg-category-trigger/10 text-category-trigger"
              : "bg-category-error/10 text-category-error",
          )}
        >
          {ok ? "ok" : "error"}
        </span>
      </div>
      {run.pathLength != null ? (
        <p className="mt-0.5 text-[10px] text-neutral-500 font-mono">
          {run.pathLength} node{run.pathLength === 1 ? "" : "s"} visited
          {run.endNodeId ? ` · ended at ${run.endNodeId}` : ""}
        </p>
      ) : null}
      {!ok && run.errorMessage ? (
        <p className="mt-0.5 text-[11px] text-category-error line-clamp-2">
          {run.errorCode ? `${run.errorCode}: ` : ""}
          {run.errorMessage}
        </p>
      ) : null}
    </li>
  );
}
